/**
 * ShopSuccess — Stripe Checkout return page.
 *
 * Reads ?session_id=cs_test_... from the URL, polls the backend status endpoint
 * every 2s (max ~30s) to confirm payment. On `paid`, clears the cart and shows
 * the confirmation with order details. On `expired`/`failed`, shows an error
 * with a link back to the Shop.
 *
 * Stripe-backed flow: the backend marks the order paid (either from this poll
 * or the webhook) and dispatches confirmation emails idempotently.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ShoppingBag, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import logger from "../lib/logger";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const STATES = {
  POLLING: "polling",
  PAID: "paid",
  EXPIRED: "expired",
  FAILED: "failed",
  TIMEOUT: "timeout",
  MISSING: "missing",
};

export default function ShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [state, setState] = useState(sessionId ? STATES.POLLING : STATES.MISSING);
  const [order, setOrder] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const { clear } = useCart();
  const cartClearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return undefined;

    let cancelled = false;
    const startedAt = Date.now();
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await api.getCheckoutStatus(sessionId);
        if (cancelled) return;
        setOrder(data);

        if (data.status === "paid") {
          if (!cartClearedRef.current) {
            clear();
            cartClearedRef.current = true;
          }
          setState(STATES.PAID);
          return;
        }
        if (data.status === "expired") {
          setState(STATES.EXPIRED);
          return;
        }
        if (data.payment_status === "failed") {
          setState(STATES.FAILED);
          return;
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Status check failed";
        if (err?.response?.status === 404) {
          setErrorMsg("Order not found.");
          setState(STATES.FAILED);
          return;
        }
        logger.warn("Checkout status poll error:", detail);
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setState(STATES.TIMEOUT);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, clear]);

  return (
    <div className="bg-demolition-black min-h-screen py-16 px-6 md:px-12" data-testid="shop-success-page">
      <div className="max-w-3xl mx-auto">
        {state === STATES.MISSING && (
          <SuccessShell tone="warn" icon={<AlertTriangle />} title="No order to look up">
            <p className="font-mono text-white/70 mt-4">
              We didn&apos;t find a checkout session in the URL. Head back to the Shop to start an order.
            </p>
            <BackToShopLink />
          </SuccessShell>
        )}

        {state === STATES.POLLING && (
          <SuccessShell tone="info" icon={<Loader2 className="animate-spin" />} title="Confirming your order...">
            <p className="font-mono text-white/70 mt-4" data-testid="checkout-polling">
              Hang tight — we&apos;re verifying your payment with Stripe. This usually takes a few seconds.
            </p>
          </SuccessShell>
        )}

        {state === STATES.PAID && order && (
          <PaidConfirmation order={order} />
        )}

        {state === STATES.EXPIRED && (
          <SuccessShell tone="warn" icon={<AlertTriangle />} title="Checkout session expired">
            <p className="font-mono text-white/70 mt-4">
              Your Stripe checkout session has expired without a payment. Nothing was charged.
            </p>
            <BackToShopLink />
          </SuccessShell>
        )}

        {state === STATES.FAILED && (
          <SuccessShell tone="warn" icon={<AlertTriangle />} title="Payment didn't go through">
            <p className="font-mono text-white/70 mt-4">
              {errorMsg || "We couldn't confirm your payment. If you were charged, contact us and we'll sort it out."}
            </p>
            <BackToShopLink />
          </SuccessShell>
        )}

        {state === STATES.TIMEOUT && (
          <SuccessShell tone="warn" icon={<AlertTriangle />} title="Still processing...">
            <p className="font-mono text-white/70 mt-4">
              Your payment is taking longer than expected. If you were charged, you&apos;ll receive a
              confirmation email shortly. Otherwise, please try again.
            </p>
            <BackToShopLink />
          </SuccessShell>
        )}
      </div>
    </div>
  );
}

function SuccessShell({ tone, icon, title, children }) {
  const borderColor = tone === "info" ? "border-demolition-chrome/40" : "border-demolition-red/60";
  const iconColor = tone === "info" ? "text-demolition-chrome" : "text-demolition-red";
  return (
    <div
      className={`bg-demolition-paper border-2 ${borderColor} p-8 md:p-12 text-center`}
      data-testid="checkout-status-shell"
    >
      <div className={`mx-auto mb-6 w-14 h-14 ${iconColor}`}>
        <span className="block w-14 h-14">{icon}</span>
      </div>
      <h1 className="font-russo text-3xl md:text-4xl text-white uppercase tracking-tighter">
        {title}
      </h1>
      {children}
    </div>
  );
}

function BackToShopLink() {
  return (
    <Link
      to="/shop"
      className="inline-flex items-center gap-2 mt-8 px-6 py-3 border-2 border-demolition-red
        text-demolition-red hover:bg-demolition-red hover:text-white
        font-russo uppercase tracking-widest text-base transition-colors"
      data-testid="back-to-shop-link"
    >
      <ShoppingBag className="w-5 h-5" />
      Back to Shop
    </Link>
  );
}

function PaidConfirmation({ order }) {
  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
  const shipping = order.shipping_address || null;
  return (
    <div
      className="bg-demolition-paper border-2 border-demolition-red p-8 md:p-12"
      data-testid="checkout-success"
    >
      <div className="text-center">
        <CheckCircle2 className="w-16 h-16 text-demolition-red mx-auto mb-4" />
        <h1 className="font-russo text-4xl md:text-5xl text-white uppercase tracking-tighter">
          Order Confirmed
        </h1>
        <p className="font-teko text-xl text-demolition-chrome uppercase tracking-widest mt-2">
          Thanks for supporting Demolition
        </p>
        {order.customer_email && (
          <p
            className="font-mono text-sm text-white/70 mt-4 inline-flex items-center gap-1.5"
            data-testid="checkout-email"
          >
            <Mail className="w-4 h-4" />
            Confirmation sent to {order.customer_email}
          </p>
        )}
      </div>

      <div className="border-t border-white/10 mt-8 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="font-russo text-base text-white uppercase tracking-tight mb-2">Order ID</h2>
          <p className="font-mono text-sm text-white/70 break-all" data-testid="checkout-order-id">
            {order.order_id || order.id}
          </p>
        </div>
        {shipping && (
          <div>
            <h2 className="font-russo text-base text-white uppercase tracking-tight mb-2">Shipping To</h2>
            <address className="font-mono text-sm text-white/70 not-italic" data-testid="checkout-shipping">
              {shipping.name && <div>{shipping.name}</div>}
              {shipping.line1 && <div>{shipping.line1}</div>}
              {shipping.line2 && <div>{shipping.line2}</div>}
              <div>
                {[shipping.city, shipping.state, shipping.postal_code].filter(Boolean).join(", ")}
              </div>
              {shipping.country && <div>{shipping.country}</div>}
            </address>
          </div>
        )}
      </div>

      {lineItems.length > 0 && (
        <div className="mt-8">
          <h2 className="font-russo text-base text-white uppercase tracking-tight mb-3">Items</h2>
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {lineItems.map((li, idx) => (
              <li
                key={`${li.item_id || idx}-${idx}`}
                className="flex items-center justify-between py-3 font-mono text-sm"
                data-testid={`checkout-line-${idx}`}
              >
                <span className="text-white/80">
                  {li.name} <span className="text-white/40">× {li.quantity}</span>
                </span>
                <span className="text-white">{fmt((li.unit_amount || 0) * li.quantity / 100)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 space-y-1 font-mono text-sm">
        <div className="flex justify-between text-white/70">
          <span>Shipping</span>
          <span>{fmt(order.shipping_total)}</span>
        </div>
        <div className="flex justify-between text-white/70">
          <span>Tax</span>
          <span>{fmt(order.tax_total)}</span>
        </div>
        <div className="flex justify-between items-baseline border-t border-white/10 pt-2 mt-2">
          <span className="font-russo text-white uppercase tracking-tight text-base">Total</span>
          <span
            className="font-russo text-demolition-red text-2xl"
            data-testid="checkout-total"
          >
            {fmt(order.amount_total)}
          </span>
        </div>
      </div>

      <div className="text-center mt-8">
        <BackToShopLink />
      </div>
    </div>
  );
}
